import {ChangeEvent, FormEvent, useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {AxiosError} from 'axios'
import {getUserByToken, login, updateUserProfile, uploadUserProfilePhoto} from '../core/_requests'
import {useAuth} from '../core/Auth'
import * as authHelper from '../core/AuthHelpers'
import {AuthModel, SmsChallengeResponse, UserModel} from '../core/_models'
import {ensureDeviceId} from '../core/device'
import {DEFAULT_AUTHENTICATED_ROUTE} from '../../../routing/constants'

export const CBBEE_PROFILE_STORAGE_KEY = 'cbee-user-profile'
export const LOGIN_USERNAME_PLACEHOLDER = 'E-posta veya kullanıcı adı'

export type CbeeUserProfile = {
  firstName: string
  lastName: string
  roleLevel: string
  positionName: string
  image?: string
  imageUrl?: string
}

const defaultCbeeProfile: CbeeUserProfile = {
  firstName: 'Gürkan',
  lastName: 'Erdoğan',
  roleLevel: 'Admin',
  positionName: '',
}

type LoginView = 'signIn' | 'userOperationsAuth' | 'userOperationsProfile'

type PendingSession = {
  tokens: AuthModel[]
  firstRoleAuth: AuthModel
  user: UserModel
}

type LoginProps = {
  initialView?: LoginView
  onProfileComplete?: () => void
}

export const getStoredCbeeUserProfile = (): CbeeUserProfile => {
  if (typeof localStorage === 'undefined') {
    return defaultCbeeProfile
  }

  try {
    const rawProfile = localStorage.getItem(CBBEE_PROFILE_STORAGE_KEY)
    return rawProfile ? {...defaultCbeeProfile, ...JSON.parse(rawProfile)} : defaultCbeeProfile
  } catch (error) {
    console.error('CBEE PROFILE LOCAL STORAGE PARSE ERROR', error)
    return defaultCbeeProfile
  }
}

export const saveStoredCbeeUserProfile = (profile: CbeeUserProfile) => {
  if (typeof localStorage === 'undefined') {
    return
  }

  try {
    localStorage.setItem(CBBEE_PROFILE_STORAGE_KEY, JSON.stringify(profile))
    window.dispatchEvent(new Event('cbee-profile-updated'))
  } catch (error) {
    console.error('CBEE PROFILE LOCAL STORAGE SAVE ERROR', error)
  }
}

const applyProfileToUser = (user: UserModel, profile: CbeeUserProfile): UserModel => {
  const displayName = [profile.firstName, profile.lastName]
    .map((value) => value.trim())
    .filter(Boolean)
    .join(' ')
  const positionName = profile.positionName.trim()

  return {
    ...user,
    fullName: displayName || user.fullName,
    fullname: displayName || user.fullname,
    title: positionName || user.title,
    applicationUserTitle: positionName || user.applicationUserTitle,
  }
}

const loginSchema = Yup.object().shape({
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  userName: '',
  password: '',
}

export type LoginFailureResponse = SmsChallengeResponse & {message?: string}

const genericLoginError = 'The login detail is incorrect'

export const getLoginFailureMessage = (error: AxiosError<LoginFailureResponse>) => {
  if (!error.response && error.request) {
    return 'Giriş sunucusuna ulaşılamıyor. İnternet bağlantınızı veya kurum ağı erişimini kontrol edin.'
  }

  return error.response?.data?.message ?? genericLoginError
}

export function Login({initialView = 'signIn', onProfileComplete}: LoginProps = {}) {
  const [loading, setLoading] = useState(false)
  const [requiresSms, setRequiresSms] = useState(false)
  const [smsCode, setSmsCode] = useState('')
  const [smsDetails, setSmsDetails] = useState<SmsChallengeResponse | null>(null)
  const [deviceId] = useState(() => ensureDeviceId())
  const [view, setView] = useState<LoginView>(initialView)
  const [pendingSession, setPendingSession] = useState<PendingSession | null>(null)
  const [profile, setProfile] = useState<CbeeUserProfile>(() => getStoredCbeeUserProfile())
  const [showPassword, setShowPassword] = useState(false)
  const [profileCurrentPassword, setProfileCurrentPassword] = useState('')
  const [profilePassword, setProfilePassword] = useState('')
  const [profilePasswordConfirm, setProfilePasswordConfirm] = useState('')
  const [profileError, setProfileError] = useState<string | null>(null)
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null)
  const {saveAuth, setCurrentUser} = useAuth()
  const navigate = useNavigate()

  const commitSession = (tokens: AuthModel[], firstRoleAuth: AuthModel, user: UserModel) => {
    const adminRoleIndex = tokens.findIndex((token) => token.role?.toUpperCase() === 'ADMIN')
    const activeRoleIndex = adminRoleIndex >= 0 ? adminRoleIndex : 0
    const activeRoleAuth = tokens[activeRoleIndex] || firstRoleAuth

    authHelper.setAuthRoles({
      roles: tokens,
      activeRoleIndex,
    })
    saveAuth(activeRoleAuth, activeRoleIndex)
    setCurrentUser(user)
  }

  const buildProfileFromUser = (user: UserModel, auth: AuthModel): CbeeUserProfile => {
    const storedProfile = getStoredCbeeUserProfile()
    const fullName = user.fullName || user.fullname || ''
    const [firstName, ...lastNameParts] = fullName.trim().split(/\s+/).filter(Boolean)

    return {
      ...storedProfile,
      firstName: storedProfile.firstName || firstName || defaultCbeeProfile.firstName,
      lastName: storedProfile.lastName || lastNameParts.join(' ') || defaultCbeeProfile.lastName,
      roleLevel:
        storedProfile.roleLevel || user.roleName || auth.role || defaultCbeeProfile.roleLevel,
      positionName:
        storedProfile.positionName ||
        user.title ||
        user.applicationUserTitle ||
        defaultCbeeProfile.positionName,
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setStatus(undefined)

      try {
        const shouldEnterImmediately = view === 'signIn'
        const trimmedCode = smsCode.trim()
        const response = await login(
          values.userName,
          values.password,
          undefined,
          requiresSms && trimmedCode.length > 0 ? trimmedCode : undefined,
          deviceId
        )

        const responseData = response.data as AuthModel[] | SmsChallengeResponse
        if ((responseData as SmsChallengeResponse).requiresSms) {
          const challenge = responseData as SmsChallengeResponse
          setRequiresSms(true)
          setSmsDetails(challenge)
          const messageText = challenge.message ?? 'SMS doğrulaması gerekli.'
          const phoneInfo = challenge.phone ? ` (${challenge.phone})` : ''
          setStatus(`${messageText}${phoneInfo}`)

          if (trimmedCode.length > 0) {
            setSmsCode('')
          }

          setSubmitting(false)
          setLoading(false)
          return
        }

        const tokens = responseData as AuthModel[]
        const firstRoleAuth = tokens[0]
        if (!firstRoleAuth) {
          throw new Error('Token alınamadı.')
        }

        const {data: user} = await getUserByToken(firstRoleAuth.token)
        setRequiresSms(false)
        setSmsDetails(null)
        setSmsCode('')

        if (shouldEnterImmediately) {
          commitSession(tokens, firstRoleAuth, user)
          return
        }

        setPendingSession({tokens, firstRoleAuth, user})
        setProfileCurrentPassword(values.password)
        setProfile(buildProfileFromUser(user, firstRoleAuth))
        setProfileError(null)
        setView('userOperationsProfile')
      } catch (err) {
        const error = err as AxiosError<LoginFailureResponse>
        console.error(error)

        if (view === 'signIn') {
          saveAuth(undefined)
          setCurrentUser(undefined)
          authHelper.removeAuthRoles()
        }

        if (error.response) {
          const data = error.response.data
          if (data?.requiresSms) {
            setRequiresSms(true)
            setSmsDetails(data)
            const messageText = data.message ?? 'SMS doğrulaması gerekli.'
            const phoneInfo = data.phone ? ` (${data.phone})` : ''
            setStatus(`${messageText}${phoneInfo}`)
            setSubmitting(false)
            setLoading(false)
            return
          }
        }

        setStatus(getLoginFailureMessage(error))
      } finally {
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const handleUserOperationsClick = () => {
    formik.setStatus(undefined)
    formik.setFieldValue('password', '')
    setRequiresSms(false)
    setSmsDetails(null)
    setSmsCode('')
    setView('userOperationsAuth')
  }

  const handleProfileImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setProfile((currentProfile) => ({...currentProfile, image: reader.result as string}))
        setProfileImageFile(file)
      }
    }
    reader.readAsDataURL(file)
  }

  const handleProfileComplete = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setProfileError(null)

    if (profilePassword || profilePasswordConfirm) {
      if (!profileCurrentPassword) {
        setProfileError('Şifre değiştirmek için mevcut şifrenizi girin.')
        return
      }

      if (profilePassword.length < 8) {
        setProfileError('Yeni şifre en az 8 karakter olmalı.')
        return
      }

      if (profilePassword !== profilePasswordConfirm) {
        setProfileError('Yeni şifre ve tekrar alanı aynı olmalı.')
        return
      }
    }

    let nextProfile = profile

    try {
      const token = pendingSession?.firstRoleAuth.token

      if (profileImageFile) {
        const {data} = await uploadUserProfilePhoto(profileImageFile, token)
        const stableImage = data.imageUrl || data.image
        nextProfile = {...nextProfile, image: stableImage, imageUrl: data.imageUrl || stableImage}
      }

      const {data: savedProfile} = await updateUserProfile(
        nextProfile,
        token,
        profilePassword || undefined,
        profileCurrentPassword || undefined
      )
      nextProfile = {
        ...nextProfile,
        firstName: savedProfile.firstName || nextProfile.firstName,
        lastName: savedProfile.lastName || nextProfile.lastName,
        roleLevel: savedProfile.roleLevel || nextProfile.roleLevel,
        positionName: savedProfile.positionName || nextProfile.positionName,
      }

      saveStoredCbeeUserProfile(nextProfile)
      setProfile(nextProfile)
      setProfileImageFile(null)
      setProfileCurrentPassword('')
      setProfilePassword('')
      setProfilePasswordConfirm('')

      if (pendingSession) {
        commitSession(
          pendingSession.tokens,
          pendingSession.firstRoleAuth,
          applyProfileToUser(pendingSession.user, nextProfile)
        )
        setPendingSession(null)
        navigate(DEFAULT_AUTHENTICATED_ROUTE, {replace: true})
        return
      }

      setCurrentUser((currentUser) =>
        currentUser ? applyProfileToUser(currentUser, nextProfile) : currentUser
      )

      if (onProfileComplete) {
        onProfileComplete()
        return
      }

      navigate(DEFAULT_AUTHENTICATED_ROUTE, {replace: true})
    } catch (error) {
      console.error(error)
      setProfileError('Profil bilgileri kaydedilemedi. Lütfen tekrar deneyin.')
    }
  }

  if (view === 'userOperationsProfile') {
    return (
      <form
        className='form w-100 cbee-auth-form cbee-user-operations-form'
        onSubmit={handleProfileComplete}
      >
        <div className='cbee-auth-form-heading cbee-user-operations-heading'>
          <span>Kullanıcı işlemleri</span>
        </div>

        {profileError && (
          <div className='alert alert-info'>
            <div className='alert-text'>{profileError}</div>
          </div>
        )}

        <div className='cbee-user-image-upload'>
          <div className='cbee-user-image-preview'>
            {profile.image || profile.imageUrl ? (
              <img src={profile.image || profile.imageUrl} alt='Kullanıcı' />
            ) : (
              <span>{profile.firstName.charAt(0)}</span>
            )}
          </div>
          <label className='cbee-user-image-button'>
            Resim yükle
            <input type='file' accept='image/*' onChange={handleProfileImageChange} />
          </label>
        </div>

        <div className='row g-3'>
          <div className='col-6'>
            <label className='form-label fw-bolder text-dark fs-6'>Adı</label>
            <input
              className='form-control form-control-lg form-control-solid'
              value={profile.firstName}
              onChange={(event) => setProfile({...profile, firstName: event.target.value})}
            />
          </div>
          <div className='col-6'>
            <label className='form-label fw-bolder text-dark fs-6'>Soyadı</label>
            <input
              className='form-control form-control-lg form-control-solid'
              value={profile.lastName}
              onChange={(event) => setProfile({...profile, lastName: event.target.value})}
            />
          </div>
        </div>

        <div className='fv-row'>
          <label className='form-label fw-bolder text-dark fs-6'>Kullanıcı yetki seviyesi</label>
          <input
            className='form-control form-control-lg form-control-solid'
            value={profile.roleLevel}
            onChange={(event) => setProfile({...profile, roleLevel: event.target.value})}
          />
        </div>

        <div className='fv-row'>
          <label className='form-label fw-bolder text-dark fs-6'>Pozisyon adı</label>
          <input
            className='form-control form-control-lg form-control-solid'
            value={profile.positionName}
            onChange={(event) => setProfile({...profile, positionName: event.target.value})}
          />
        </div>

        <div className='fv-row'>
          <label className='form-label fw-bolder text-dark fs-6'>Şifre yenileme</label>
          <input
            className='form-control form-control-lg form-control-solid mb-3'
            type='password'
            autoComplete='current-password'
            placeholder='Mevcut şifre'
            value={profileCurrentPassword}
            onChange={(event) => setProfileCurrentPassword(event.target.value)}
          />
          <input
            className='form-control form-control-lg form-control-solid mb-3'
            type='password'
            autoComplete='new-password'
            placeholder='Yeni şifre'
            value={profilePassword}
            onChange={(event) => setProfilePassword(event.target.value)}
          />
          <input
            className='form-control form-control-lg form-control-solid'
            type='password'
            autoComplete='new-password'
            placeholder='Yeni şifre tekrar'
            value={profilePasswordConfirm}
            onChange={(event) => setProfilePasswordConfirm(event.target.value)}
          />
        </div>

        <button type='submit' className='btn btn-lg cbee-auth-submit w-100 mt-2'>
          İşlemleri tamamla ve sisteme gir
        </button>
      </form>
    )
  }

  const isUserOperationsAuth = view === 'userOperationsAuth'

  return (
    <form
      className='form w-100 cbee-auth-form'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      <div className='cbee-auth-form-heading'>
        <span>{isUserOperationsAuth ? 'Kullanıcı işlemleri' : 'Hoş geldiniz'}</span>
      </div>

      {formik.status ? (
        <div className='mb-lg-15 alert alert-info'>
          <div className='alert-text'>{formik.status}</div>
        </div>
      ) : null}

      <div className='fv-row mb-10'>
        <label className='form-label fs-6 fw-bolder text-dark'>E-posta veya kullanıcı adı</label>
        <input
          placeholder={LOGIN_USERNAME_PLACEHOLDER}
          {...formik.getFieldProps('userName')}
          className={clsx('form-control form-control-lg form-control-solid')}
          type='text'
          name='userName'
          autoComplete='off'
          disabled={loading}
        />
      </div>

      <div className='fv-row mb-10'>
        <div className='d-flex justify-content-between mt-n5'>
          <div className='d-flex flex-stack mb-2'>
            <label className='form-label fw-bolder text-dark fs-6 mb-0'>Şifre</label>
          </div>
        </div>
        <div className='cbee-auth-password-field'>
          <input
            placeholder='Şifre'
            type={showPassword ? 'text' : 'password'}
            autoComplete='off'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {
                'is-invalid': formik.touched.password && formik.errors.password,
              },
              {
                'is-valid': formik.touched.password && !formik.errors.password,
              }
            )}
            disabled={loading}
          />
          <button
            type='button'
            className='cbee-auth-password-toggle'
            aria-label={showPassword ? 'Sifreyi gizle' : 'Sifreyi goster'}
            aria-pressed={showPassword}
            onClick={() => setShowPassword((current) => !current)}
            disabled={loading}
          >
            <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
          </button>
        </div>
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>

      {requiresSms && (
        <div className='fv-row mb-10'>
          <label className='form-label fw-bolder text-dark fs-6'>SMS Doğrulama Kodu</label>
          <input
            placeholder='SMS kodu'
            type='text'
            autoComplete='one-time-code'
            value={smsCode}
            onChange={(event) => setSmsCode(event.target.value)}
            className='form-control form-control-lg form-control-solid'
            disabled={loading}
          />
          <div className='form-text'>
            {smsDetails?.phone
              ? `${smsDetails.phone} numarasına gönderilen kodu giriniz.`
              : 'Telefonunuza gönderilen kodu giriniz.'}
            {smsDetails?.resendAfterSeconds
              ? ` Kod yeniden gönderilmeden önce ${smsDetails.resendAfterSeconds} saniye bekleyin.`
              : ''}
          </div>
        </div>
      )}

      <div className='text-center'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-lg cbee-auth-submit w-100 mb-5'
          disabled={formik.isSubmitting || !formik.isValid || loading}
        >
          {!loading && <span className='indicator-label'>Şifre ile devam et</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Kontrol ediliyor...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>

        {!isUserOperationsAuth && (
          <button
            type='button'
            className='cbee-user-operations-button'
            onClick={handleUserOperationsClick}
            disabled={loading}
          >
            Kullanıcı işlemleri
          </button>
        )}

        {isUserOperationsAuth && (
          <button
            type='button'
            className='cbee-user-operations-button'
            onClick={() => setView('signIn')}
            disabled={loading}
          >
            Giriş ekranına dön
          </button>
        )}

        <Link className='cbee-auth-forgot' to='/auth/forgot-password'>
          Şifremi unuttum / yenile
        </Link>
      </div>
    </form>
  )
}
